// Heart Games Hook
// Main hook for managing Heart Games state and operations

import { useState, useEffect, useCallback } from 'react';
import { HeartGame, GameProgress, GameResult, UseHeartGamesReturn } from '../../types/heartGames';
import { GameService, ProgressService } from '../../services/HeartGames';
import { logger } from '../../utils/logger';

export const useHeartGames = (userId?: string): UseHeartGamesReturn => {
  const [games, setGames] = useState<HeartGame[]>([]);
  const [currentGame, setCurrentGame] = useState<HeartGame | null>(null);
  const [progress, setProgress] = useState<GameProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load games on mount
  useEffect(() => {
    loadGames();
  }, []);

  // Load progress when userId changes
  useEffect(() => {
    if (userId) {
      loadProgress();
    }
  }, [userId]);

  const loadGames = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const gamesData = await GameService.getGames();
      setGames(gamesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load games');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadProgress = useCallback(async () => {
    if (!userId) return;

    try {
      setIsLoading(true);
      setError(null);
      const progressData = await ProgressService.getUserProgress(userId);
      setProgress(progressData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const startGame = useCallback(async (gameId: string) => {
    if (!userId) {
      setError('User ID is required to start a game');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Get game details
      const gameData = await GameService.getGame(gameId);
      setCurrentGame(gameData);
      
      // Start game session
      await GameService.startGameSession(gameId, userId);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start game');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const completeGame = useCallback(async (results: GameResult) => {
    if (!userId) {
      setError('User ID is required to complete a game');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Update progress
      const updatedProgress = await ProgressService.updateProgress(userId, results);
      setProgress(updatedProgress);
      
      // Clear current game
      setCurrentGame(null);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete game');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const pauseGame = useCallback(() => {
    // Implementation for pausing game
    logger.log('Game paused');
  }, []);

  const resumeGame = useCallback(() => {
    // Implementation for resuming game
    logger.log('Game resumed');
  }, []);

  const exitGame = useCallback(() => {
    setCurrentGame(null);
    setError(null);
  }, []);

  return {
    games,
    currentGame,
    progress,
    isLoading,
    error,
    startGame,
    completeGame,
    pauseGame,
    resumeGame,
    exitGame
  };
};









